import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'components/ui/grid.scss';

const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

const colInfix = (bp) => (bp === 'xs' ? '' : `-${bp}`);

const colClassesFor = (bp, val) => {
  if (val === undefined || val === null || val === false) return null;
  const infix = colInfix(bp);
  if (val === true) return `ui-col${infix}`;
  if (val === 'auto') return `ui-col${infix}-auto`;
  if (typeof val === 'number') return `ui-col${infix}-${val}`;
  if (typeof val === 'string') return `ui-col${infix}-${val}`;
  if (typeof val === 'object') {
    const out = [];
    const { size, offset, order } = val;
    if (size === 'auto') out.push(`ui-col${infix}-auto`);
    else if (size === true) out.push(`ui-col${infix}`);
    else if (size !== undefined) out.push(`ui-col${infix}-${size}`);
    if (offset !== undefined) out.push(`ui-offset${infix}-${offset}`);
    if (order !== undefined) out.push(`ui-order${infix}-${order}`);
    return out;
  }
  return null;
};

export const Col = forwardRef(({ tag: Tag = 'div', className, children, xs, sm, md, lg, xl, xxl, ...rest }, ref) => {
  const classes = ['ui-col-base'];
  let hasAnySize = false;
  const props = { xs, sm, md, lg, xl, xxl };
  BREAKPOINTS.forEach((bp) => {
    const c = colClassesFor(bp, props[bp]);
    if (c) {
      hasAnySize = true;
      if (Array.isArray(c)) classes.push(...c);
      else classes.push(c);
    }
  });
  if (!hasAnySize) classes.push('ui-col');

  return (
    <Tag ref={ref} className={classNames(classes, className)} {...rest}>
      {children}
    </Tag>
  );
});
Col.displayName = 'Col';
const colSizeProp = PropTypes.oneOfType([
  PropTypes.bool,
  PropTypes.number,
  PropTypes.oneOf(['auto']),
  PropTypes.shape({
    size: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.oneOf(['auto'])]),
    offset: PropTypes.number,
    order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
]);
Col.propTypes = {
  tag: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node,
  xs: colSizeProp,
  sm: colSizeProp,
  md: colSizeProp,
  lg: colSizeProp,
  xl: colSizeProp,
  xxl: colSizeProp,
};

export const Row = forwardRef(({ tag: Tag = 'div', className, children, noGutters, ...rest }, ref) => (
  <Tag
    ref={ref}
    className={classNames('ui-row', { 'ui-row--no-gutters': noGutters }, className)}
    {...rest}
  >
    {children}
  </Tag>
));
Row.displayName = 'Row';
Row.propTypes = {
  tag: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node,
  noGutters: PropTypes.bool,
};

export default Row;
